import { Flex, Text } from '@chakra-ui/layout'
import { useToast } from '@chakra-ui/toast'
import {
  addEdge,
  Background, Connection, Edge, ReactFlow, ReactFlowProvider, useEdgesState, useNodesState
} from '@xyflow/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FaSave } from 'react-icons/fa'
import { MdRestartAlt } from 'react-icons/md'
import 'reactflow/dist/style.css'
import { ButtonCmp } from '../../../../../components/ButtonCmp'
import LoadingContainer from '../../../../../components/LoadingContainer'
import { ModalBase } from '../../../../../components/ModalBase'
import { ModalDelete } from '../../../../../components/ModalDelete'
import { SelectCmp } from '../../../../../components/SelectCmp'
import { useAuth } from '../../../../../contexts/AuthContext'
import { CargoOrganogramaResponse, iNomeId, iTrilhaCarreiraCompleta } from '../../../../../interfaces'
import { appApi } from '../../../../../services/appApi'
import { parseJwt } from '../../../../../services/token'
import { aplicarLayoutHierarquico, mapearCargosPorId, montarGrafoDoLayoutSalvo, montarGrafoPadraoDeCargos, montarLigacoesDoCadastro } from './BuildReactFlowGraph'
import CustomNode from './CustomNode'

const tiposDeNo = {
  custom: CustomNode
}

interface iTrilhaCarreiraFlowProps {
  cargos: CargoOrganogramaResponse[]
  departamentoId: string
  departamentos: iNomeId[]
  aoSalvarLigacoes: () => void
  somenteLeitura?: boolean
}

interface iLigacaoCargos {
  superiorId: string
  subordinadoId: string
  areaId: string | null
}

interface iConexaoPendente {
  conexao: Edge | Connection
  superior: string
  subordinado: string
  areas: iNomeId[]
}

const TrilhaCarreiraFlow: React.FC<iTrilhaCarreiraFlowProps> = ({ cargos, departamentoId, departamentos, aoSalvarLigacoes, somenteLeitura }) => {
  const toast = useToast()
  const { Permissoes } = useAuth()
  const [Nos, setNos, aplicarMudancaNosNos] = useNodesState<any>([])
  const [Ligacoes, setLigacoes, aplicarMudancaNasLigacoes] = useEdgesState<any>([])

  const [LayoutSalvo, setLayoutSalvo] = useState<iTrilhaCarreiraCompleta>()
  const [CarregandoLayout, setCarregandoLayout] = useState(true)
  const [AlteracoesPendentes, setAlteracoesPendentes] = useState(false)
  const [ModalRestaurarAberto, setModalRestaurarAberto] = useState(false)
  const [ModalLigacoesAberto, setModalLigacoesAberto] = useState(false)
  const [ConexaoPendente, setConexaoPendente] = useState<iConexaoPendente>()
  const [AreaSelecionada, setAreaSelecionada] = useState('')

  const instanciaFluxo = useRef<any>(null)

  const podeGerenciar = parseJwt().adm === 'True' || Permissoes.funcionalidades?.some(e => e.tipo === 6 && e.liberado)
  const visaoGeral = departamentoId === ''

  function obterLayoutSalvo(exibirCarregamento: boolean): void {
    // A equipe é só um recorte dos cargos: o layout salvo é da trilha geral e trataria os demais nós como removidos
    if (somenteLeitura) {
      setLayoutSalvo(undefined)
      setCarregandoLayout(false)
      return
    }

    if (exibirCarregamento) {
      setCarregandoLayout(true)
    }

    appApi.get(`api/TrilhaCarreiraNode?departamentoId=${departamentoId}`)
      .then(res => {
        setLayoutSalvo(res.status === 200 ? res.data : undefined)
      })
      .catch(err => console.log(err))
      .finally(() => {
        if (exibirCarregamento) {
          setCarregandoLayout(false)
        }
      })
  }

  function salvarLayout(aoConcluir?: () => void): void {
    const layout = {
      departamentoId: departamentoId === '' ? null : departamentoId,
      nodes: Nos.map((no: any) => ({
        id: no.id,
        type: no.type,
        data: { cargoId: no.data?.id ?? no.id },
        position: no.position,
        width: no.width ?? no.measured?.width ?? 350,
        height: no.height ?? no.measured?.height ?? 100
      })),
      viewport: instanciaFluxo.current?.getViewport()
    }

    appApi.post('api/TrilhaCarreiraNode', layout).then(() => {
      setAlteracoesPendentes(false)
      obterLayoutSalvo(false)
      toast({ title: 'Trilha de carreira salva com sucesso', status: 'success', position: 'top', duration: 4000 })
    }).catch(err => console.log(err))
      .finally(() => aoConcluir?.())
  }

  function calcularDiferencaDeLigacoes(): { adicionadas: iLigacaoCargos[], removidas: iLigacaoCargos[] } {
    const chave = (ligacao: any): string => `${ligacao.source}|${ligacao.target}`

    const doCadastro = montarLigacoesDoCadastro(cargos)
    const chavesDoCadastro = new Set(doCadastro.map(chave))
    const chavesDaTela = new Set(Ligacoes.map(chave))

    // Na visão geral a remoção vale para todas as áreas do par (areaId null).
    return {
      adicionadas: Ligacoes
        .filter((ligacao: any) => !chavesDoCadastro.has(chave(ligacao)))
        .map((ligacao: any) => ({ superiorId: ligacao.source, subordinadoId: ligacao.target, areaId: visaoGeral ? ligacao.data?.areaId : departamentoId })),
      removidas: doCadastro
        .filter(ligacao => !chavesDaTela.has(chave(ligacao)))
        .map(ligacao => ({ superiorId: ligacao.source, subordinadoId: ligacao.target, areaId: visaoGeral ? null : departamentoId }))
    }
  }

  function salvarTrilha(): void {
    const { adicionadas, removidas } = calcularDiferencaDeLigacoes()
    if (adicionadas.length === 0 && removidas.length === 0) {
      salvarLayout()
      return
    }
    setModalLigacoesAberto(true)
  }

  function salvarLigacoes(): void {
    const { adicionadas, removidas } = calcularDiferencaDeLigacoes()

    appApi.put('api/CargosESalarios/TrilhaCarreira/Ligacoes', { adicionadas, removidas })
      .then(() => salvarLayout(aoSalvarLigacoes))
      .catch(err => {
        const mensagem = err?.response?.data?.mensagem
        if (err?.response?.status === 409) {
          const nomes = (err.response.data?.cargos ?? []).map((cargo: { nome: string }) => cargo.nome).join(', ')
          toast({ title: mensagem ?? 'Há cargos com ajuste de trilha pendente', description: nomes, status: 'error', position: 'top', duration: 8000 })
        } else if (err?.response?.status === 400 && mensagem) {
          toast({ title: mensagem, status: 'error', position: 'top', duration: 6000 })
        } else {
          console.log(err)
        }
      })
  }

  function restaurarLayoutPadrao(): void {
    appApi.delete(`api/TrilhaCarreiraNode?departamentoId=${departamentoId}`).then(() => {
      setLayoutSalvo(undefined)
      setAlteracoesPendentes(false)
      toast({ title: 'Layout padrão restaurado com sucesso', status: 'success', position: 'top', duration: 4000 })
    }).catch(err => console.log(err))
  }

  const marcarAlteracao = useCallback(() => setAlteracoesPendentes(true), [])

  // Só chega aqui um nó de cargo que sumiu do cadastro. O deleteElements do
  // React Flow já leva junto as ligações e dispara o onNodesDelete.
  const excluirNoRemovido = useCallback((cargoId: string) => {
    void instanciaFluxo.current?.deleteElements({ nodes: [{ id: cargoId }] })
  }, [])

  // O React Flow emite mudanças de 'dimensions' também na medição inicial dos nós,
  // por isso só o redimensionamento feito pelo usuário marca alteração pendente.
  const aoAlterarNos = useCallback((mudancas: any[]) => {
    if (mudancas.some(mudanca => mudanca.type === 'dimensions' && mudanca.resizing === true)) {
      setAlteracoesPendentes(true)
    }
    aplicarMudancaNosNos(mudancas)
  }, [aplicarMudancaNosNos])

  const aoConectar = useCallback((conexao: Edge | Connection) => {
    if (!visaoGeral) {
      setLigacoes((ligacoesAtuais: any) => addEdge(conexao, ligacoesAtuais))
      setAlteracoesPendentes(true)
      return
    }

    if (Ligacoes.some((ligacao: any) => ligacao.source === conexao.source && ligacao.target === conexao.target)) return

    const cargoPorId = mapearCargosPorId(cargos)
    const superior = cargoPorId.get(conexao.source)
    const subordinado = cargoPorId.get(conexao.target)
    const areas = departamentos.filter(departamento => superior?.areas?.includes(departamento.id))
    const areasEmComum = areas.filter(area => subordinado?.areas?.includes(area.id))

    setAreaSelecionada(areas.length === 1 ? areas[0].id : areasEmComum.length === 1 ? areasEmComum[0].id : '')
    setConexaoPendente({ conexao, superior: superior?.nomeCargo ?? '', subordinado: subordinado?.nomeCargo ?? '', areas })
  }, [setLigacoes, visaoGeral, Ligacoes, cargos, departamentos])

  function confirmarAreaDaLigacao(): void {
    if (!ConexaoPendente) return
    setLigacoes((ligacoesAtuais: any) => addEdge({ ...ConexaoPendente.conexao, data: { areaId: AreaSelecionada } }, ligacoesAtuais))
    setAlteracoesPendentes(true)
    setConexaoPendente(undefined)
  }

  useEffect(() => {
    setAlteracoesPendentes(false)
    obterLayoutSalvo(true)
  }, [departamentoId])

  const grafoSalvo = useMemo(
    () => LayoutSalvo ? montarGrafoDoLayoutSalvo(LayoutSalvo, cargos, excluirNoRemovido) : undefined,
    [cargos, LayoutSalvo, excluirNoRemovido]
  )
  const possuiCargosNovos = grafoSalvo?.possuiCargosNovos ?? false

  useEffect(() => {
    if (grafoSalvo && !grafoSalvo.possuiCargosNovos) {
      setNos(grafoSalvo.nos as any)
      setLigacoes(grafoSalvo.ligacoes as any)
      return
    }

    const { nos: nosPadrao, ligacoes: ligacoesPadrao } = montarGrafoPadraoDeCargos(cargos)
    setNos(aplicarLayoutHierarquico(cargos, nosPadrao) as any)
    setLigacoes(ligacoesPadrao as any)
  }, [cargos, grafoSalvo])

  return (
    <Flex flexDir="column" gap="1rem" alignItems="end">
      {podeGerenciar && (
        <Flex gap={'.5rem'}>
          {LayoutSalvo && (
            <ButtonCmp VarColor='c5' leftIcon={<MdRestartAlt />} onClick={() => setModalRestaurarAberto(true)}>
              Restaurar layout padrão
            </ButtonCmp>
          )}
          <ButtonCmp VarColor='Rosa' leftIcon={<FaSave />} isDisabled={!AlteracoesPendentes && !possuiCargosNovos} onClick={salvarTrilha}>
            Salvar trilha
          </ButtonCmp>
        </Flex>
      )}

      <ModalDelete
        isOpen={ModalRestaurarAberto}
        onRequestClose={() => setModalRestaurarAberto(false)}
        onConfirm={restaurarLayoutPadrao}
        center
        message={'O layout personalizado desta visualização será descartado e a trilha voltará a ser gerada automaticamente pelo cadastro de cargos. Deseja continuar?'}
      />

      <ModalDelete
        isOpen={ModalLigacoesAberto}
        onRequestClose={() => setModalLigacoesAberto(false)}
        onConfirm={salvarLigacoes}
        center
        message={`As alterações nas ligações serão gravadas no cadastro dos cargos. Os descritivos já assinados dos cargos afetados passarão a constar como desatualizados.${ModalLigacoesAberto && visaoGeral && calcularDiferencaDeLigacoes().removidas.length > 0 ? ' Na visão geral, a remoção de uma ligação vale para todas as áreas em que ela existe.' : ''} Deseja continuar?`}
      />

      <ModalBase
        isOpen={!!ConexaoPendente}
        onClose={() => setConexaoPendente(undefined)}
        Titulo='Área da ligação'
        Width='28rem'
      >
        <Flex flexDir={'column'} gap={'1rem'} p={'0 1.5rem 1.25rem 1.5rem'}>
          <Text fontSize={'.875rem'}>
            Selecione a área em que o cargo <strong>{ConexaoPendente?.subordinado}</strong> ficará subordinado ao cargo <strong>{ConexaoPendente?.superior}</strong>.
          </Text>
          <SelectCmp title='Área' value={AreaSelecionada} OnChange={setAreaSelecionada}>
            <option value={''}>Selecione</option>
            {ConexaoPendente?.areas.map(area => <option key={area.id} value={area.id}>{area.nome}</option>)}
          </SelectCmp>
          <Flex justifyContent={'end'} gap={'.5rem'}>
            <ButtonCmp VarColor='c5' onClick={() => setConexaoPendente(undefined)}>Cancelar</ButtonCmp>
            {AreaSelecionada !== '' && <ButtonCmp VarColor='Green2' onClick={confirmarAreaDaLigacao}>Confirmar</ButtonCmp>}
          </Flex>
        </Flex>
      </ModalBase>

      <LoadingContainer loading={CarregandoLayout} linhas={20}>
        <ReactFlowProvider key={`${departamentoId}-${LayoutSalvo?.id ?? 'padrao'}`}>
          <ReactFlow
            nodes={Nos}
            edges={Ligacoes}
            onNodesChange={aoAlterarNos}
            onEdgesChange={aplicarMudancaNasLigacoes}
            onConnect={aoConectar}
            onNodeDragStop={marcarAlteracao}
            onEdgesDelete={marcarAlteracao}
            onNodesDelete={marcarAlteracao}
            onInit={(instancia) => { instanciaFluxo.current = instancia }}
            nodeTypes={tiposDeNo as any}
            nodesDraggable={!somenteLeitura}
            nodesConnectable={!somenteLeitura}
            deleteKeyCode={somenteLeitura ? null : undefined}
            minZoom={0.05}
            fitView={!LayoutSalvo?.viewport || possuiCargosNovos}
            defaultViewport={LayoutSalvo?.viewport}
            style={{
              minHeight: '78vh',
              maxHeight: '78vh',
              border: '1px solid var(--c6)'
            }}
            defaultEdgeOptions={{
              style: { stroke: '#1961E0', strokeWidth: 2 }
            }}
          >
            <Background />
          </ReactFlow>
        </ReactFlowProvider>
      </LoadingContainer>
    </Flex>
  )
}

export default TrilhaCarreiraFlow
